import { Session } from 'src_common/sdk/session';
import {
    openapiProxyCustomerWalletRequest,
    OpenapiProxyCustomerWalletResponse200Type,
} from 'src/api_openapi/generated/openapi_proxy_customer_wallet';
import {
    openapiProxyCustomerFreeBetsRequest,
    OpenapiProxyCustomerFreeBetsResponse200Type,
} from 'src/api_openapi/generated/openapi_proxy_customer_free_bets';
import {
    openapiProxyCustomerTransactionHistoryRequest,
    OpenapiProxyCustomerTransactionHistoryResponse200Type,
    OpenapiProxyCustomerTransactionHistoryParamsType,
} from 'src/api_openapi/generated/openapi_proxy_customer_transaction_history';
import {
    OpenapiProxyCustomerAllBetsParamsType,
    openapiProxyCustomerAllBetsRequest,
    OpenapiProxyCustomerAllBetsResponse200Type,
} from 'src/api_openapi/generated/openapi_proxy_customer_all_bets';

import {
    openapiProxyCustomerBonusesRequest,
    OpenapiProxyCustomerBonusesResponse200Type,
} from 'src/api_openapi/generated/openapi_proxy_customer_bonuses';
import {
    openapiProxyCustomerNetDepositRequest,
    OpenapiProxyCustomerNetDepositParamsType,
    OpenapiProxyCustomerNetDepositResponse200Type,
} from 'src/api_openapi/generated/openapi_proxy_customer_net_deposit';
import { Resource, Result } from 'src_common/common/mobx-utils/Resource';
import { MobxMapAutoNew } from 'src_common/common/mobx-utils/MobxMapAutoNew';
import {
    openapiProxyAnonymousFindPageRequest,
    OpenapiProxyAnonymousFindPageResponse200Type,
} from 'src/api_openapi/generated/openapi_proxy_anonymous_find_page';
import {
    OpenapiProxyCustomerDepositTransactionCallbackRealexParamsType,
    openapiProxyCustomerDepositTransactionCallbackRealexRequest,
    OpenapiProxyCustomerDepositTransactionCallbackRealexResponse200Type,
} from 'src/api_openapi/generated/openapi_proxy_customer_deposit_transaction_callback_realex';

import {
    OpenapiProxyCustomerWithdrawalsParamsType,
    openapiProxyCustomerWithdrawalsRequest,
    OpenapiProxyCustomerWithdrawalsResponse200Type,
} from 'src/api_openapi/generated/openapi_proxy_customer_withdrawals';

import {
    OpenapiProxyCustomerBankingConfigsParamsType,
    openapiProxyCustomerBankingConfigsRequest,
    OpenapiProxyCustomerBankingConfigsResponse200Type,
} from 'src/api_openapi/generated/openapi_proxy_customer_banking_configs';

import {
    OpenapiProxyAnonymousRequestPasswordResetParamsType,
    openapiProxyAnonymousRequestPasswordResetRequest,
    OpenapiProxyAnonymousRequestPasswordResetResponse200Type,
} from 'src/api_openapi/generated/openapi_proxy_anonymous_request_password_reset';
import {
    OpenapiProxyAnonymousPasswordResetParamsType,
    openapiProxyAnonymousPasswordResetRequest,
    OpenapiProxyAnonymousPasswordResetResponse200Type,
} from 'src/api_openapi/generated/openapi_proxy_anonymous_password_reset';
import {
    openapiProxyCustomerDepositRequest,
    OpenapiProxyCustomerDepositResponse200Type,
    OpenapiProxyCustomerDepositParamsType,
} from 'src/api_openapi/generated/openapi_proxy_customer_deposit';
import {
    openapiProxyCustomerSrgQuestionsOnlyRequest,
    OpenapiProxyCustomerSrgQuestionsOnlyResponse200Type,
    OpenapiProxyCustomerSrgQuestionsOnlyParamsType,
} from 'src/api_openapi/generated/openapi_proxy_customer_srg_questions_only';
import {
    openapiProxyCustomerSrgValidateRequest,
    OpenapiProxyCustomerSrgValidateResponse200Type,
    OpenapiProxyCustomerSrgValidateParamsType,
} from 'src/api_openapi/generated/openapi_proxy_customer_srg_validate';
import {
    getRollingLimits,
    getDepositLimits,
    getRealityCheck,
    putRealityCheck,
} from 'src_server/trpc/types/responsibleGambling';

import { action, computed, makeObservable, observable } from 'mobx';
import { CurrencySymbolType, CurrencyType } from 'src_common/common/amount/website-money/currency';
import { Amount } from 'src_common/common/amount/Amount';
import { LocalStorageState } from 'src/domains/layouts/state/localStorage/LocalStorageState';
import { Common } from 'src/domains/common/Common';
import {
    BasicDataModel,
    AccountBasicDataType as AccountBasicDataTypeNew,
    UserBasicDataType as UserBasicDataTypeNew,
} from './BasicDataModel';
import { ExternalApi } from 'src/domains/layouts/state/externalApi/ExternalApi';
import { DateTime } from 'src_common/utils/time/time';
import { AutoWeakMap } from 'src_common/common/mobx-utils/AutoWeakMap';
import { ConfigType } from 'src/domains/layouts/config/features/types';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';

export type UserBasicDataType = UserBasicDataTypeNew;
export type AccountBasicDataType = AccountBasicDataTypeNew;
export type WalletDataType = OpenapiProxyCustomerWalletResponse200Type;
export type CustomerFreeBetsType = OpenapiProxyCustomerFreeBetsResponse200Type;
export type CustomerTransactionHistoryType = OpenapiProxyCustomerTransactionHistoryResponse200Type;
export type CustomerNetDepositType = OpenapiProxyCustomerNetDepositResponse200Type;
export type TransactionCallbackRealexType = OpenapiProxyCustomerDepositTransactionCallbackRealexResponse200Type;
export type PageCMSType = OpenapiProxyAnonymousFindPageResponse200Type['result'];
export type SrgQuestionsOnlyType = OpenapiProxyCustomerSrgQuestionsOnlyResponse200Type;
export type SrgValidationType = OpenapiProxyCustomerSrgValidateResponse200Type;

type ResourceCustomerSettingsType = {
    allowEphemeral: boolean;
};
export class ResourceCustomer<T> {
    private readonly resource: Resource<T>;

    public constructor(
        private readonly label: string,
        private readonly session: Session,
        private readonly fetchData: () => Promise<T>,
        private readonly settings?: ResourceCustomerSettingsType
    ) {
        this.resource = new Resource(fetchData);
    }

    public fetch(): Promise<T> {
        if (this.session.isAuthorized || this.isEphemeralAuthorized) {
            return this.fetchData();
        }

        console.error(
            `ResourceSession: ${this.label} => fetch => anable to retrieve data from this resource because user is not logged in`
        );
        return new Promise(() => {});
    }

    public get(): Result<T> {
        if (this.session.isAuthorized || this.isEphemeralAuthorized) {
            return this.resource.get();
        } else {
            return {
                type: 'loading',
                whenReady: new Promise(() => {}),
            };
        }
    }

    public get valueReady(): T | null {
        if (this.session.isAuthorized || this.isEphemeralAuthorized) {
            const data = this.get();

            if (data.type === 'ready') {
                return data.value;
            }
        }

        return null;
    }

    @computed private get isEphemeralAuthorized(): boolean {
        return this.settings?.allowEphemeral === true && this.session.isEphemeralActive;
    }

    public async refreshAndWait(): Promise<void> {
        return this.resource.refresh();
    }

    public refresh(): void {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.resource.refresh();
    }

    @action public clear = (): void => {
        this.resource.clear();
    };
}

interface SdkCustomerConfigType {
    canDownloadTerms: boolean;
}

export type WalletDataTypeAmountType = {
    balance: string;
    createdAt: string;
    creditLimit: string;
    currency: string;
    fundsLocked: string;
    id: string;
    lastBetAt: string;
    operatorAccumulatedProfit: string;
    operatorBalance: string;
    operatorExternalProfit: string;
    operatorProfit: string;
    playableBalance: string;
    /**
     * @deprecated - Please use this method "playableBalance" attribute
     */
    playableBalanceOld: number;
    profit: string;
    universe: string;
    updatedAt: string;
    withdrawableBalance: string;
    backofficeWithdrawableBalance: string;
};

enum ComplianceRestrictionSource {
    POF = 'pof',
    SRG = 'srg',
}

export type RollingNetDepositLimitDataType = Map<number, getRollingLimits.LimitsObjectSchemaType>;

export class UsersState {
    public static get = AutoWeakMap.create((common: Common) => new UsersState(common));
    /**
     * @deprecated - please use Common.session
     */
    public readonly session: Session;
    public readonly walletData: ResourceCustomer<WalletDataTypeAmountType>; //WalletDataType>;
    public readonly freeBetsData: ResourceCustomer<CustomerFreeBetsType | null>;
    public readonly depositLimitsData: ResourceCustomer<getDepositLimits.ResponseType>;
    public readonly rollingNetDepositLimitData: ResourceCustomer<RollingNetDepositLimitDataType>;
    public readonly realityCheckData: ResourceCustomer<getRealityCheck.SuccessResponseType | null>;
    public readonly accountBonusesData: ResourceCustomer<OpenapiProxyCustomerBonusesResponse200Type>;
    public readonly config: SdkCustomerConfigType;
    private readonly newStaticPage: MobxMapAutoNew<string, Resource<PageCMSType | null>>;
    private readonly localStorageState: LocalStorageState;
    private readonly configTypes: ConfigType;

    @observable public isSrgModalOpen: boolean;
    @observable private isPofRequestedPopupOpen: boolean;
    @observable private isPofRequestedWithoutCrPopupOpen: boolean;
    @observable private isPofFailedPopupOpen: boolean;

    private constructor(private readonly common: Common) {
        makeObservable(this);
        const session = common.session;
        this.session = common.session;
        this.configTypes = ConfigComponents.get(common).config;
        this.config = {
            canDownloadTerms: this.configTypes.termsAndConditionsDownloadLink,
        };
        this.isSrgModalOpen = this.configTypes.srgQuestionnaire;
        this.localStorageState = LocalStorageState.get(common);

        //Check if Pof modal already showed, if no show modal
        const pofPopupsLocalStorage = this.localStorageState.pofPopups.getValue();
        this.isPofRequestedPopupOpen =
            pofPopupsLocalStorage.isPofRequestedModalAlreadyShowed === false && this.configTypes.pofModals === true;
        this.isPofRequestedWithoutCrPopupOpen =
            pofPopupsLocalStorage.isPofisPofRequestedWithoutCRModalAlreadyShowed === false &&
            this.configTypes.pofModals === true;
        this.isPofFailedPopupOpen =
            pofPopupsLocalStorage.isPofFailedModalAlreadyShowed === false && this.configTypes.pofModals === true;

        this.walletData = new ResourceCustomer('walletData', session, async () => {
            const response = await this.session.call(openapiProxyCustomerWalletRequest, {});

            return {
                id: response.id,
                createdAt: response.createdAt,
                currency: response.currency,
                lastBetAt: response.lastBetAt,
                universe: response.universe,
                updatedAt: response.updatedAt,
                balance: response.balancePrecise,
                creditLimit: response.creditLimitPrecise,
                fundsLocked: response.fundsLockedPrecise,
                operatorAccumulatedProfit: response.operatorAccumulatedProfitPrecise,
                operatorBalance: response.operatorBalancePrecise,
                operatorExternalProfit: response.operatorExternalProfitPrecise,
                operatorProfit: response.operatorProfitPrecise,
                profit: response.profitPrecise,
                withdrawableBalance: response.withdrawableBalancePrecise,
                playableBalance: response.playableBalancePrecise,
                playableBalanceOld: parseInt(response.balancePrecise.replace('.', ''), 10),
                backofficeWithdrawableBalance: response.backofficeWithdrawableBalancePrecise,
            };
        });

        this.freeBetsData = new ResourceCustomer('freeBetsData', session, async () => {
            return await this.session.call(openapiProxyCustomerFreeBetsRequest, {});
        });

        this.depositLimitsData = new ResourceCustomer('depositLimitsData', session, async () => {
            const response = await this.common.trpcClient.client.responsibleGamblingRouter.getDepositLimits.query();
            return response;
        });

        this.rollingNetDepositLimitData = new ResourceCustomer('rollingNetDepositLimitData', session, async () => {
            const payload = await this.common.trpcClient.client.responsibleGamblingRouter.getRollingLimits.query();

            if (payload.responseStatus === 'success') {
                return new Map(
                    payload.data
                        .sort((a, b) => a.windowLengthInDays - b.windowLengthInDays)
                        .map((activeObj) => [activeObj.windowLengthInDays, activeObj])
                );
            } else {
                return new Map();
            }
        });

        this.realityCheckData = new ResourceCustomer('realityCheckData', session, async () => {
            const response = await this.common.trpcClient.client.responsibleGamblingRouter.getRealityCheck.query();

            if (response.responseStatus === 'success') {
                return response.data;
            }

            return null;
        });

        this.accountBonusesData = new ResourceCustomer(
            'accountBonusesData',
            session,
            async (): Promise<OpenapiProxyCustomerBonusesResponse200Type> => {
                return this.session.call(openapiProxyCustomerBonusesRequest, {});
            }
        );

        this.newStaticPage = new MobxMapAutoNew(
            (pageId: string) =>
                new Resource(async (): Promise<PageCMSType | null> => {
                    const data = await this.session.call(openapiProxyAnonymousFindPageRequest, {
                        requestBody: {
                            slug: pageId,
                            // Leave en-gb language for now
                            page_language: 'en-gb',
                        },
                    });

                    return data.result ?? null;
                })
        );
    }

    @action public toggleSrgModal = (): void => {
        this.isSrgModalOpen = !this.isSrgModalOpen;
    };

    @computed public get isSrgRequested(): boolean {
        return this.basicData.valueReady?.srgStatus === 'requested';
    }

    @action public togglePofFailedPopup = (): void => {
        this.isPofFailedPopupOpen = !this.isPofFailedPopupOpen;
        this.localStorageState.pofPopups.setValue({
            ...this.localStorageState.pofPopups.getValue(),
            isPofFailedModalAlreadyShowed: true,
        });
    };

    @action public togglePofRequestedPopup = (): void => {
        this.isPofRequestedPopupOpen = !this.isPofRequestedPopupOpen;
        this.localStorageState.pofPopups.setValue({
            ...this.localStorageState.pofPopups.getValue(),
            isPofRequestedModalAlreadyShowed: true,
        });
    };

    @action public togglePofRequestedWithoutCRPopup = (): void => {
        this.isPofRequestedWithoutCrPopupOpen = !this.isPofRequestedWithoutCrPopupOpen;
        this.localStorageState.pofPopups.setValue({
            ...this.localStorageState.pofPopups.getValue(),
            isPofisPofRequestedWithoutCRModalAlreadyShowed: true,
        });
    };

    @computed public get isPofFailedModal(): boolean {
        const cr = this.basicData.valueReady?.complianceRestriction?.source;

        const isFailedPofStatus = this.basicData.valueReady?.pofStatus === 'failed';
        const isCompRestiction = cr === ComplianceRestrictionSource.POF;

        return isFailedPofStatus && isCompRestiction && this.isPofFailedPopupOpen;
    }

    @computed public get isPofRequestedModal(): boolean {
        const cr = this.basicData.valueReady?.complianceRestriction?.source;

        const isRequestedPofStatus = this.basicData.valueReady?.pofStatus === 'requested';
        const isCompRestiction = cr === ComplianceRestrictionSource.POF;

        return isRequestedPofStatus && isCompRestiction && this.isPofRequestedPopupOpen;
    }

    @computed public get isPofRequestedWithoutCRModal(): boolean {
        const cr = this.basicData.valueReady?.complianceRestriction?.source;

        const isRequestedPofStatus = this.basicData.valueReady?.pofStatus === 'requested';
        const isNoCompRestiction = cr !== ComplianceRestrictionSource.POF;

        return isRequestedPofStatus && isNoCompRestiction && this.isPofRequestedWithoutCrPopupOpen;
    }

    @computed public get isMonthlyActivityPopupOpen(): boolean {
        const basicData = BasicDataModel.get(this.common).basicDataReady;
        const monthlyActivityAt = basicData?.monthlyActivityReportLastConfirmedAt ?? null;

        if (basicData === null) {
            return false;
        }

        if (monthlyActivityAt === null) {
            return true;
        }

        const firstDayOfCurrentMonth = DateTime.current().startOfMonths();
        const monthlyActivityDate = DateTime.from(monthlyActivityAt);

        if (monthlyActivityDate !== null && firstDayOfCurrentMonth.isAfter(monthlyActivityDate)) {
            return true;
        }

        return false;
    }

    /**
     * @deprecated - please use BasicDataModel.get(this.common).basicData
     */
    public get basicData(): UserBasicDataType {
        return BasicDataModel.get(this.common).basicData;
    }

    public getPage(slug: string): Result<PageCMSType | null> {
        const newPage = this.newStaticPage.get(slug).get();

        if (newPage.type === 'ready') {
            return {
                type: 'ready',
                value: newPage.value,
            };
        }

        if (newPage.type === 'loading') {
            return newPage;
        }
        return {
            type: 'ready',
            value: null,
        };
    }

    /**
     * @deprecated - please use BasicDataModel.get(this.common).oddsFormatShort
     */
    @computed public get oddsFormatShort(): 'f' | 'd' {
        return BasicDataModel.get(this.common).oddsFormatShort;
    }

    /**
     * @deprecated - please use BasicDataModel.get(this.common).currency
     */
    @computed public get currency(): CurrencyType {
        return BasicDataModel.get(this.common).currency;
    }

    /**
     * @deprecated - please use  BasicDataModel.get(this.common).money(amount, withoutZeros)
     */
    public money = (amount: Amount | null | undefined, withoutZeros?: boolean): string => {
        return BasicDataModel.get(this.common).money(amount, withoutZeros);
    };

    /**
     * @deprecated - please use BasicDataModel.get(this.common).moneySymbol
     */
    @computed public get moneySymbol(): CurrencySymbolType {
        return BasicDataModel.get(this.common).moneySymbol;
    }

    public postAllBets(
        params: OpenapiProxyCustomerAllBetsParamsType
    ): Promise<OpenapiProxyCustomerAllBetsResponse200Type> {
        return this.session.call(openapiProxyCustomerAllBetsRequest, params);
    }

    public postTransactionHistory(
        params: OpenapiProxyCustomerTransactionHistoryParamsType
    ): Promise<CustomerTransactionHistoryType> {
        return this.session.call(openapiProxyCustomerTransactionHistoryRequest, params);
    }

    public postNetDeposit(params: OpenapiProxyCustomerNetDepositParamsType): Promise<CustomerNetDepositType> {
        return this.session.call(openapiProxyCustomerNetDepositRequest, params);
    }

    public withdraw(
        params: OpenapiProxyCustomerWithdrawalsParamsType
    ): Promise<OpenapiProxyCustomerWithdrawalsResponse200Type> {
        return this.session.call(openapiProxyCustomerWithdrawalsRequest, params);
    }

    public bankingConfigs(
        params: OpenapiProxyCustomerBankingConfigsParamsType
    ): Promise<OpenapiProxyCustomerBankingConfigsResponse200Type> {
        return this.session.call(openapiProxyCustomerBankingConfigsRequest, params);
    }

    public transactionCallbackRealex(
        params: OpenapiProxyCustomerDepositTransactionCallbackRealexParamsType
    ): Promise<TransactionCallbackRealexType> {
        return this.session.call(openapiProxyCustomerDepositTransactionCallbackRealexRequest, params);
    }

    public requestResetPassword(
        params: OpenapiProxyAnonymousRequestPasswordResetParamsType
    ): Promise<OpenapiProxyAnonymousRequestPasswordResetResponse200Type> {
        return this.session.call(openapiProxyAnonymousRequestPasswordResetRequest, params);
    }

    public resetPassword(
        params: OpenapiProxyAnonymousPasswordResetParamsType
    ): Promise<OpenapiProxyAnonymousPasswordResetResponse200Type> {
        return this.session.call(openapiProxyAnonymousPasswordResetRequest, params);
    }

    public depositInitiate(
        params: OpenapiProxyCustomerDepositParamsType
    ): Promise<OpenapiProxyCustomerDepositResponse200Type> {
        return this.session.call(openapiProxyCustomerDepositRequest, params, {
            'X-Advertisement-Id': ExternalApi.get(this.common).adjustAdID ?? '',
        });
    }

    public srgQuestionsOnly(params: OpenapiProxyCustomerSrgQuestionsOnlyParamsType): Promise<SrgQuestionsOnlyType> {
        return this.session.call(openapiProxyCustomerSrgQuestionsOnlyRequest, params);
    }

    public srgValidate(params: OpenapiProxyCustomerSrgValidateParamsType): Promise<SrgValidationType> {
        return this.session.call(openapiProxyCustomerSrgValidateRequest, params);
    }

    public async setRealityCheck(
        params: putRealityCheck.InputType
    ): Promise<putRealityCheck.SuccessResponseType | null> {
        const response = await this.common.trpcClient.client.responsibleGamblingRouter.putRealityCheck.mutate(params);

        if (response.responseStatus === 'success') {
            this.realityCheckData.refresh();
            return response.data;
        }

        return null;
    }

    @action public onChangeRingFencedFlag = async (): Promise<boolean | null> => {
        const response = await this.common.trpcClient.client.accounts.changeRingFencedFunds.mutate({
            ringFencedFunds: true,
        });

        if (response.responseStatus === 'success') {
            this.basicData.refresh();
            return true;
        }

        return false;
    };
}
